<template>
    <div style="min-height: calc(100vh - 158px);" class="card">
        <div style="background: #DBE7F2" class="p-2">
            <TitleButton
                :showBtn="false"
                :showAddNew="false"
                btnTitle="Add New"
                title="Voucher List"
            />

            <div class="row match-height mt-1">
                <DateQuerySetter @onClickGo="getPayments">
                    <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                        <label for="colFormLabel" class="col-form-label">Voucher type: </label>
                        <select class="form-control" v-model="voucherType">
                            <option :value="null" selected>Select voucher type</option>
                            <option
                                v-for="voucherTypeKey in Object.keys(voucherTypes)"
                                :key="voucherTypeKey"
                                :value="voucherTypeKey"
                            >
                                {{ voucherTypes[voucherTypeKey] }}
                            </option>
                        </select>
                    </div>

                    <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                        <label for="colFormLabel" class="col-form-label">Txn type: </label>
                        <select class="form-control" v-model="txnType">
                            <option :value="null" selected>Select txn type</option>
                            <option
                                v-for="txnTypeKey in Object.keys(txnTypes)"
                                :key="txnTypeKey"
                                :value="txnTypeKey"
                            >
                                {{ txnTypes[txnTypeKey] }}
                            </option>
                        </select>
                    </div>

                    <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                        <label for="colFormLabel" class="col-form-label">Business</label>
                        <v-select
                            placeholder="Select Business"
                            class="w-100"
                            :options="businesses"
                            label="name"
                            :reduce="(business) => business.id"
                            v-model="businessId"
                        />
                    </div>

                    <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                        <label for="colFormLabel" class="col-form-label">Cost Center: </label>
                        <v-select
                            placeholder="Select Cost Centre"
                            v-model="costCentreId"
                            :options="costCentres"
                            label="name"
                            :reduce="costCentre => costCentre.id"
                        />
                    </div>

                    <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label for="colFormLabel" class="col-form-label">Project</label>
                        <v-select
                            placeholder="Select Project"
                            v-model="projectId"
                            :options="projects"
                            label="name"
                            :reduce="project => project.id"
                        />
                    </div>

                    <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                        <label for="colFormLabel" class="col-form-label">Created By: </label>
                        <v-select
                            placeholder="Created By"
                            class="w-100"
                            :options="contactList"
                            label="name"
                            :reduce="(contact) => contact.id"
                            v-model="createdBy"
                        />
                    </div>

                    <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                        <label for="colFormLabel" class="col-form-label">Voucher Number: </label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="voucherNo"
                            placeholder="Voucher Number"
                        >
                    </div>
                </DateQuerySetter>
            </div>
        </div>

        <ul class="nav nav-tabs mt-2 px-2" role="tablist">
            <li class="nav-item" v-for="tab in tabs" :key="tab.key">
                <a
                    href="#"
                    class="nav-link"
                    :class="{ active: activeTab === tab.key }"
                    @click.prevent="changeTab(tab.key)"
                >
                    {{ tab.label }}
                </a>
            </li>
        </ul>

        <div class="d-flex flex-wrap align-items-center justify-content-between m-2 mb-0">
            <div class="d-flex flex-wrap gap-1">
                <button
                    v-for="filter in voucherTypeFilters"
                    :key="filter.key"
                    type="button"
                    class="btn btn-sm"
                    :class="voucherType === filter.value ? 'btn-primary' : 'btn-outline-primary'"
                    @click="changeVoucherTypeFilter(filter.value)"
                >
                    {{ filter.label }}
                </button>
            </div>

            <button
                v-if="activeTab === 'pending'"
                :disabled="disableApprovalBtn"
                class="btn btn-success"
                @click="handleJournalApproval"
            >
                Approve Selected
            </button>
        </div>

        <ListTable :showSelect="activeTab === 'pending'" @approved="getPayments" />
        <div class="mb-2"></div>

        <div class="px-2 position-absolute bottom-0">
            <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination" />
        </div>

        <Loader v-if="loading" />
    </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import handleJournal from '@/services/modules/journal'
import handleReport from "@/services/modules/accountingReport"
import handleProjects from "@/services/modules/procurement/project"
import handleImportTracker from "@/services/modules/procurement/importTracker"
import handleCBusinesses from "@/services/modules/businesses"
import handleCostCentres from "@/services/modules/ngo/costCentre"

import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import ListTable from '@/components/molecule/company/voucher/approval/ListTable.vue'
import Pagination from '@/components/atom/Pagination'
import Loader from '@/components/atom/LoaderComponent'

const route = useRoute()
const router = useRouter()
const store = useStore()

const showError = inject('showError')
const showSuccess = inject('showSuccess')

const tabs = [
    { key: 'pending', label: 'Pending', status: 'pending_approval' },
    { key: 'return', label: 'Return', status: 'return,returned' },
    { key: 'rejected', label: 'Rejected', status: 'rejected' },
    { key: 'approved', label: 'Approved', status: 'approved' },
]
const activeTab = ref('pending')

const offset = ref(20)
const contactList = ref([])
const accountHeads = ref([])
const businesses = ref([])
const projects = ref([])
const voucherTypes = ref({})
const txnTypes = ref({})
const costCentres = ref([])

const contactProfileId = ref(null)
const voucherType = ref(null)
const txnType = ref(null)
const costCentreId = ref(null)
const createdBy = ref(null)
const voucherNo = ref(null)
const accountHeadId = ref(null)
const businessId = ref(null)
const projectId = ref(null)

const { fetchProjects } = handleProjects()
const { fetchBusinessList } = handleCBusinesses()
const { fetchCostCentreList } = handleCostCentres()
const { fetchContacts } = handleImportTracker()
const { fetchHome } = handleReport()
const { handleApproval, fetchJournals, loading, fetchAccountHeads } = handleJournal()

const journals = computed(() => store.state.journal.journals)
const companyId = computed(() => route.params.companyId)
const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const page = computed(() => route.query.page)

const voucherTypeFilters = computed(() => {
    const filters = Object.keys(voucherTypes.value).map(key => ({
        key,
        label: voucherTypes.value[key],
        value: key,
    }))

    filters.push({ key: 'all_vouchers', label: 'All Vouchers', value: null })
    return filters
})

const disableApprovalBtn = computed(() => {
    if (journals.value.length === 0) {
        return true
    }

    return journals.value.filter(journal => journal.selected === true).length === 0
})

const setJournals = (data) => store.commit('journal/setJournals', data)
const setPaginationData = (data) => store.commit('setPaginationData', data)
const resetPagination = () => store.commit('resetPagination')

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const currentStatus = computed(() => {
    const tab = tabs.find(t => t.key === activeTab.value)
    return tab ? tab.status : 'pending_approval'
})

const changeTab = async (key) => {
    if (activeTab.value === key) return
    activeTab.value = key
    const routeQuery = { ...route.query, page: undefined }
    await router.push({ path: route.path, query: routeQuery })
    await getPayments()
}

const changeVoucherTypeFilter = async (value) => {
    if (voucherType.value === value) return
    voucherType.value = value
    const routeQuery = { ...route.query, page: undefined }
    await router.push({ path: route.path, query: routeQuery })
    await getPayments()
}

const getQuery = () => {
    const params = {
        company_id: companyId.value,
        status: currentStatus.value,
        start_date: start.value || undefined,
        end_date: end.value || undefined,
        offset: start.value || end.value ? offset.value : undefined,
        page: page.value || undefined,
        contact_profile_id: contactProfileId.value || undefined,
        account_head_id: accountHeadId.value || undefined,
        business_id: businessId.value || undefined,
        project_id: projectId.value || undefined,
        voucher_type: voucherType.value || undefined,
        txn_type: txnType.value || undefined,
        cost_centre_id: costCentreId.value || undefined,
        created_by: createdBy.value || undefined,
        voucher_no: voucherNo.value || undefined,
        all_vouchers: start.value || end.value ? 'yes' : undefined,
    }

    return '?' + new URLSearchParams(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ).toString()
}

const getPayments = async () => {
    const query = getQuery()

    try {
        loading.value = true
        const res = await fetchJournals(query)

        if (!res.status) {
            showError(res.message)
            return
        }

        if (res.status) {
            setJournals(res.data.data)

            if (res.data.data.length > 0) {
                setPagination(res.data)
            } else {
                resetPagination()
            }
        }
    } catch (err) {
        if (!err.response) {
            showError('Something is wrong. Check your connectivity!!')
            return
        }

        showError(err.response ? err.response.message : 'Something went wrong')
    } finally {
        loading.value = false
    }
}

const onPageChange = async (pageNo) => {
    const routeQuery = { ...route.query, page: pageNo }

    await router.push({
        path: route.path,
        query: routeQuery,
    })

    await getPayments()
}

const handleJournalApproval = async () => {
    const voucherIds = journals.value
        .filter(journal => journal.selected === true)
        .map(journal => journal.id)

    if (!voucherIds.length) {
        showError('Please select at least one voucher')
        return
    }

    const confirmed = window.confirm('Are you sure you want to approve the selected voucher(s)?')

    if (!confirmed) {
        return
    }

    try {
        const res = await handleApproval({
            company_id: route.params.companyId,
            voucher_ids: voucherIds,
            status: 'approved'
        })

        showSuccess(res.message)
        onPageChange(1)
    } catch (err) {
        if (err.response && err.response.data) {
            showError(err.response.data.message)
            return
        }

        showError('Something went wrong please try again later')
    }
}

onMounted(async () => {
    loading.value = true

    const currentCompanyId = route.params.companyId
    const contactId = ''
    const q = ''
    const companyQuery = '?company_id=' + currentCompanyId
    const query = companyQuery + '&q=' + q + '&contact_id=' + contactId
    const projectQuery = companyQuery + '&with_donors=1'

    try {
        await getPayments()

        await Promise.all([
            fetchContacts(companyQuery).then(res => {
                if (res.data) {
                    contactList.value = res.data
                }
            }),
            fetchCostCentreList(companyQuery).then(res => {
                if (res.data) {
                    costCentres.value = res.data
                }
            }),
            fetchHome().then(res => {
                if (res.status) {
                    voucherTypes.value = res.data.voucher_type
                    txnTypes.value = res.data.txn_type
                }
            }),
            fetchAccountHeads(currentCompanyId).then(res => {
                if (res.data) {
                    accountHeads.value = res.data
                }
            }),
            fetchProjects(projectQuery).then(res => {
                if (res.data) {
                    projects.value = res.data
                }
            }),
            fetchBusinessList(companyQuery).then(res => {
                if (res.data) {
                    businesses.value = res.data
                }
            }),
        ])
    } catch (err) {
        showError(err.message)
    } finally {
        loading.value = false
    }
})
</script>